import {
  Component,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
  Element,
} from '@stencil/core';
import {
  BasketHelper,
  BasketModel,
  BasketService,
  BasketRepository,
  DateFormat,
  DateUtil,
  Language,
  MONEY_SYMBOLS,
  MoneyPrice,
  PricesForStayPeriod,
  RateInformation,
  RoomAvailability,
  RoomBasketModel,
  RoomHelper,
  RoomRepository,
  RoomModel,
  RoomService,
  SessionDisplay,
  SessionHelper,
  SessionRepository,
  SessionModel,
  SessionService,
  SessionStayPeriod,
  SessionHelperService,
} from '@cesare';
import {
  switchMap,
  distinctUntilChanged,
  map,
  debounceTime,
  first,
  take,
  finalize,
} from 'rxjs/operators';
import { of, zip, combineLatest, tap, Subscription } from 'rxjs';
import { isEqual } from 'lodash-es';
import {
  QW_ROOM_CODE,
  QwChangeRoomEvent,
  QwRoomBaseInfoType,
  QwRoomListCardButtonType,
  QwRoomListOrderType,
  QwRoomListType,
  QwWeekCalendarDirection,
} from '../../index';
import {
  sessionStorageGetItem,
  sessionStorageRemoveItem,
} from '../../core/storage/storage.service';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';
import { isAddingPromo$, sessionUpdating$ } from '../../core/app/app';
import { roundPriceToTwoDecimals } from '../../core/utils';

// TODO: spostare le varie logiche di buisiness su cesare, es roomsWithPrice, sortedPrice ecc

const mockRoomsSkeleton = { roomId: 1, pictures: [], summary: [] } as any;

interface RoomModelWithFilter extends RoomModel {
  filterDisableRoom: boolean;
}
@Component({
  tag: 'qw-room-list',
  styleUrl: 'qw-room-list.scss',
  shadow: false,
})
export class QwRoomList {
  @Prop() qwRoomListType: QwRoomListType = QwRoomListType.Inline;
  @Prop() qwRoomListFilterRatesWithId: number;
  @Prop() qwRoomListOrder: QwRoomListOrderType =
    QwRoomListOrderType.AscendingPrice;
  @Prop() qwRoomListPlaceholders: string;
  @Prop() qwRoomListBaseInfoType: QwRoomBaseInfoType =
    QwRoomBaseInfoType.Inline;
  @Prop() qwRoomListImageTransformationOptions: string;
  @Prop() qwRoomListRateHighlight: RateInformation['code'];
  @Prop() qwRoomListShowAvailabilityMessage: boolean = false;
  @Prop() qwRoomListShowCarouselInCard: boolean = false;
  @Prop() currentLang: string;
  @Prop() qwRoomListPreselectedOffersWrap: string;
  @Prop() qwRoomListPreselectedOffers: any;
  @Prop() qwRoomsHtmlElements: NodeListOf<HTMLElement>;
  @State() qwRoomListHasPreselectedOffers: boolean = false;
  @State() rooms: RoomModel[] = [];
  @State() filteredRoomsByRoomType: RoomModelWithFilter[] = [];
  @State() session: SessionModel;
  @State() rangeDate: Date[];
  @State() rangeDateSession: Date[];
  @State() roomPrices: PricesForStayPeriod = {};
  @State() isPriceLoading: boolean;
  @State() nights: number;
  @State() basketIsEmpty: boolean;
  @State() basketRooms: RoomBasketModel[] = [];
  @State() basketRoom: RoomBasketModel;
  @State() basketRoomTotals: { [roomId: string]: MoneyPrice } = {};
  @State() numberOfGuests: number;
  @State() numberOfAccommodation: number;
  @State() language: SessionDisplay['culture'];
  @State() noAvailability = false;
  @State() currentRoomType: string = QW_ROOM_CODE.All;
  @State() hasSessionChildrenOrInfants: boolean = false;
  @State() componentLoading: boolean = true;
  @State() renderTriggered: boolean = false;
  @State() isLoadingRooms = true;
  @State() isAddingPromo = false;
  @State() basket: BasketModel;
  @Element() el: HTMLElement;
  @Event() qwRoomListClickRoom: EventEmitter<{
    type: QwRoomListCardButtonType;
    room: RoomModel;
  }>;
  @Event() qwEmitGtmData: EventEmitter<any>;
  @Event() qwGtmRoomListReady: EventEmitter;
  @Event() qwGtmRoomListHasFired: boolean = false;

  private startDate: Date;
  private endDate: Date;
  // private symbol: string;
  private rangeDateString: string[];
  private rangeDateStored: string[] = [];
  private todayString: string;

  private sessionSub: Subscription;

  public componentWillLoad() {
    isAddingPromo$
      .pipe(tap((isAdding) => (this.isAddingPromo = isAdding)))
      .subscribe();

    SessionService.getSession()
      .pipe(take(1), distinctUntilChanged(isEqual))
      .subscribe();

    this.componentIsLoading$.pipe(distinctUntilChanged()).subscribe((value) => {
      this.componentLoading = value;
      this.setServiceLoading(this.componentLoading);
    });

    RoomRepository.roomIsLoading$
      .pipe(
        distinctUntilChanged(),
        tap((isLoadingRooms) => {
          this.isLoadingRooms = isLoadingRooms;
        }),
      )
      .subscribe();

    if (this.qwRoomListPlaceholders) {
      this.rooms = Array(parseInt(this.qwRoomListPlaceholders)).fill(
        mockRoomsSkeleton,
      );
    }

    this.todayString = DateUtil.getDateStringFromDate(
      this.initNewDate(new Date()),
    );

    this.sessionSub = this.session$.subscribe();
    this.rooms$.subscribe();
    this.basket$.subscribe();
    this.setServiceLoading(true);

    loadingService.loadComplete$.subscribe((res) => {
      this.componentLoading = !res;
      if (res && !this.qwGtmRoomListHasFired) {
        const hasOffers = this.qwRoomListHasPreselectedOffers ? true : false;
        this.qwGtmRoomListHasFired = true;
        setTimeout(() => {
          this.qwGtmRoomListReady.emit(hasOffers);
        }, 400);
      }
    });
  }

  @Listen('qwCurrencyChanged', { target: 'window' })
  public currencyChanged(event: CustomEvent<SessionDisplay['currency']>) {
    SessionHelperService.fetchUpdateSessionDisplay(this.session.sessionId, {
      countryCode: 'FR',
      culture: 'en-US',
      currency: event.detail,
    }).subscribe(() => {
      this.rooms$.subscribe((res) => {
        const roomsWithPrice = res.filter((room) => {
          return (
            RoomHelper.getCheapestPrice(room).value ||
            this.basketRoomTotals[room.roomId]
          );
        });

        const sortedRooms = roomsWithPrice.sort((a, b) => {
          const priceA =
            RoomHelper.getCheapestPrice(a).value ||
            (this.basketRoomTotals[a.roomId] &&
              this.basketRoomTotals[a.roomId].value);
          const priceB =
            RoomHelper.getCheapestPrice(b).value ||
            (this.basketRoomTotals[b.roomId] &&
              this.basketRoomTotals[b.roomId].value);
          return this.qwRoomListOrder === QwRoomListOrderType.AscendingPrice
            ? priceA.amount - priceB.amount
            : priceB.amount - priceA.amount;
        });
        this.rooms = [...sortedRooms];
        this.currentRoomType = 'All';
        this.filterRoomsByRoomType(this.currentRoomType);
      });
    });
  }

  disconnectedCallback() {
    this.sessionSub.unsubscribe();
  }

  componentDidLoad() {
    this.qwRoomsHtmlElements = this.el.querySelectorAll(
      '.qw-room-list__card-wrapper',
    );
  }

  private session$ = SessionRepository.sessionLoaded$.pipe(
    distinctUntilChanged(isEqual),
    switchMap((session) => {
      this.isPriceLoading = true;
      this.session = session;
      this.language = session.display.culture;
      // this.symbol =
      //   MONEY_SYMBOLS[session.display.currency] || session.display.currency;
      this.nights = SessionHelper.getNumberOfNights(session);
      this.numberOfGuests = SessionHelper.getTotalGuests(session);
      this.hasSessionChildrenOrInfants =
        session.context.guests.children > 0 ||
        session.context.guests.infants > 0;
      const sessionStayPeriod = session.context.stayPeriod;
      this.rangeDateSession = DateUtil.getDatesRange(
        this.initNewDate(sessionStayPeriod.arrivalDate),
        this.initNewDate(sessionStayPeriod.departureDate),
        DateFormat.Date,
      );

      this.qwRoomListPreselectedOffersWrap
        ? this.setPreselectionOnRates(sessionStayPeriod)
        : '';
      return zip(of(session), BasketService.getBasket(session));
    }),
    switchMap(([session]) => {
      this.setServiceLoading(true); // evita che il loader sparisca e ricompaia per un attimo
      return RoomService.getRoomsAvailability(session.sessionId).pipe(
        take(1),
        tap((res) => this.getRoomAvailabilitySuccess(res)),
        switchMap(() => {
          return RoomService.getRooms(session.sessionId).pipe(
            take(1),
            tap(() => sessionUpdating$.next(false)),
          );
        }),
        finalize(() => {
          tap(() => sessionUpdating$.next(false));
          this.setServiceLoading(false);
        }),
      );
    }),
    tap((rooms) => {
      this.getRoomsSearchForRangeSuccess(rooms);
    }),
    finalize(() => {
      tap(() => sessionUpdating$.next(false));
      this.setServiceLoading(false);
    }),
  );

  private rooms$ = RoomRepository.roomLoaded$.pipe(
    tap((res) => {
      const roomsWithPrice = res.filter((room) => {
        return (
          RoomHelper.getCheapestPrice(room).value ||
          this.basketRoomTotals[room.roomId]
        );
      });

      const sortedRooms = roomsWithPrice.sort((a, b) => {
        const priceA =
          RoomHelper.getCheapestPrice(a).value ||
          (this.basketRoomTotals[a.roomId] &&
            this.basketRoomTotals[a.roomId].value);
        const priceB =
          RoomHelper.getCheapestPrice(b).value ||
          (this.basketRoomTotals[b.roomId] &&
            this.basketRoomTotals[b.roomId].value);
        return this.qwRoomListOrder === QwRoomListOrderType.AscendingPrice
          ? priceA.amount - priceB.amount
          : priceB.amount - priceA.amount;
      });

      this.rooms = sortedRooms;
      this.currentRoomType = this.getRoomTypeFromSessionStorage()
        ? this.getRoomTypeFromSessionStorage()
        : 'ALL';
      this.filterRoomsByRoomType(this.currentRoomType);
      this.rangeDateSession = DateUtil.getDatesRange(
        this.initNewDate(this.session.context.stayPeriod.arrivalDate),
        this.initNewDate(this.session.context.stayPeriod.departureDate),
        DateFormat.Date,
      );

      this.renderTriggered = true;
    }),
  );

  private basket$ = BasketRepository.basketWithPrice$.pipe(
    distinctUntilChanged(isEqual),
    tap((basket) => {
      this.basket = basket;
      this.basketIsEmpty = !basket.rooms.length;
      this.basketRooms = basket.rooms;
      this.basketRoomTotals = basket.rooms.reduce((acc, room) => {
        const occupancyId = BasketHelper.getFirstOccupancyIdInBasketRoom(room);
        return {
          ...acc,
          [room.roomId]: room.occupancies[occupancyId]?.price?.converted,
        };
      }, {});
    }),
  );

  public componentIsLoading$ = combineLatest([
    RoomRepository.roomIsLoading$.pipe(first((val) => val === false)),
    SessionRepository.sessionIsLoading$,
    BasketRepository.basketIsLoading$,
    SessionRepository.sessionLoaded$,
    BasketRepository.basketWithPrice$,
    RoomRepository.roomLoaded$,
  ]).pipe(
    map(
      ([
        roomLoading,
        sessionLoading,
        basketLoading,
        sessionLoaded,
        basketLoaded,
        roomLoaded,
      ]) => {
        return (
          roomLoading ||
          sessionLoading ||
          (basketLoading &&
            !(!!sessionLoaded && !!basketLoaded && !!roomLoaded))
        );
      },
    ),
    debounceTime(0),
    distinctUntilChanged(),
  );

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'room-list',
      isLoading,
    });
  }

  private setPreselectionOnRates(stayPeriod: SessionStayPeriod) {
    this.qwRoomListPreselectedOffers = JSON.parse(
      this.qwRoomListPreselectedOffersWrap,
    );
    if (this.qwRoomListPreselectedOffers?.preselected == 'true') {
      this.qwRoomListHasPreselectedOffers = this.checkOffersPreselection(
        this.qwRoomListPreselectedOffers,
        stayPeriod,
      );
    }
  }

  private filterRoomsAlreadyInBasket(room: RoomModel) {
    const basketRooms = this.basket.rooms;
    if (!basketRooms.length) {
      return false;
    }
    const exists = basketRooms.find((item) => item.roomId === room.roomId);

    return exists ? true : false;
  }

  private getRoomsSearchForRangeSuccess(newRoomPrices) {
    const firstRoomId =
      Object.keys(newRoomPrices).length && Object.keys(newRoomPrices)[0];
    const newDatesToStore = newRoomPrices[firstRoomId]
      ? Object.keys(newRoomPrices[firstRoomId])
      : [];
    const mergedDataStored = [...this.rangeDateStored, ...newDatesToStore];
    this.rangeDateStored = [...new Set(mergedDataStored)].sort();
    this.roomPrices = this.mergeActiveRoomPricesWitNewRoomPrices(newRoomPrices);
    this.isPriceLoading = false;
  }

  private getBasketRoom(roomId: RoomModel['roomId']) {
    return this.basketRooms.find((r) => r.roomId === roomId);
  }

  private mergeActiveRoomPricesWitNewRoomPrices(
    newRoomPrices: PricesForStayPeriod,
  ) {
    if (!Object.keys(newRoomPrices).length) {
      return this.roomPrices;
    }

    return Object.keys(newRoomPrices).reduce((acc, roomId) => {
      const newRoomPriceMerged = {
        ...this.roomPrices[roomId],
        ...newRoomPrices[roomId],
      };
      return { ...acc, [roomId]: newRoomPriceMerged };
    }, {} as PricesForStayPeriod);
  }

  private getRoomsSearchForRange(
    sessionStayPeriod: SessionStayPeriod,
    currency: SessionDisplay['currency'] = this.session.display.currency,
  ) {
    const dayToRemoveToStartDate =
      sessionStayPeriod.arrivalDate <= this.todayString ? 0 : -1;
    this.startDate = DateUtil.addDaysToDate(
      dayToRemoveToStartDate,
      this.initNewDate(sessionStayPeriod.arrivalDate),
    );
    this.endDate = DateUtil.addDaysToDate(6, this.initNewDate(this.startDate));
    this.rangeDate = DateUtil.getDatesRange(
      this.startDate,
      this.endDate,
      DateFormat.Date,
    );
    this.rangeDateString = DateUtil.getDatesRange(
      this.startDate,
      this.endDate,
      DateFormat.String,
    );

    const newDateToRequest = this.rangeDateString.filter(
      (d) => !this.rangeDateStored.includes(d),
    );

    if (!newDateToRequest.length) {
      return of({});
    }

    const endDateStringPlusOneDay = DateUtil.getDateStringFromDate(
      DateUtil.addDaysToDate(
        1,
        this.initNewDate(newDateToRequest[newDateToRequest.length - 1]),
      ),
    );

    const newDateToRequestStayPeriods = DateUtil.getAllPossibleStayPeriod({
      arrivalDate: newDateToRequest[0],
      departureDate: endDateStringPlusOneDay,
    });
    return RoomService.getRoomPricesForStayPeriods(
      newDateToRequestStayPeriods,
      currency,
    ).pipe(take(1));
  }

  private checkOffersPreselection(preselection, stayPeriod: SessionStayPeriod) {
    if (preselection?.preselectedAll == 'true') return true;

    if (preselection.arrival_date && preselection.departure_date) {
      const startDatePreselection = new Date(preselection.arrival_date);
      const endDatePreselection = new Date(preselection.departure_date);

      const currentStartDate = new Date(stayPeriod.arrivalDate);
      const currentEndDate = new Date(stayPeriod.departureDate);

      return (
        startDatePreselection <= currentStartDate &&
        endDatePreselection >= currentEndDate
      );
    } else {
      return false;
    }
  }

  // private getAveragePricePerNight(roomId: RoomModel['roomId']) {
  //   if (
  //     !this.roomPrices[roomId] ||
  //     !Object.keys(this.roomPrices[roomId]).length
  //   ) {
  //     return RoomDefaultLabel.NoPrice;
  //   }

  //   const stayPeriod = this.session.context.stayPeriod;
  //   const arrivalDate = this.initNewDate(stayPeriod.arrivalDate);
  //   const departureDate = this.initNewDate(stayPeriod.departureDate);

  //   const range = DateUtil.getDatesRange(
  //     arrivalDate,
  //     departureDate,
  //     DateFormat.String,
  //   );
  //   const rangeWithoutDepartureDate = [...range];
  //   rangeWithoutDepartureDate.length = range.length - 1;

  //   const calculate = this.calculateAverage(roomId, rangeWithoutDepartureDate);

  //   if (!Number(calculate)) {
  //     return RoomDefaultLabel.NoPrice;
  //   }

  //   return `${this.symbol} ${Math.round(calculate).toString()}`;
  // }

  // private calculateAverage(
  //   roomId: RoomModel['roomId'],
  //   rangeWithoutDepartureDate: string[],
  // ) {
  //   const rangeFiltered = rangeWithoutDepartureDate.reduce(
  //     (acc, dateString) => {
  //       return this.roomPrices[roomId][dateString] ? [...acc, dateString] : acc;
  //     },
  //     [],
  //   );

  //   return (
  //     rangeFiltered.reduce((acc, date) => {
  //       const valueToAdd = this.roomPrices[roomId][date]
  //         ? this.roomPrices[roomId][date].value.amount
  //         : 0;
  //       return acc + valueToAdd;
  //     }, 0) / rangeFiltered.length
  //   );
  // }

  clickButton = (type: QwRoomListCardButtonType, room: RoomModel) => {
    this.qwRoomListClickRoom.emit({ type, room });
  };

  private initNewDate(date: string | Date) {
    return DateUtil.removeTimeFromDate(new Date(date));
  }

  setRoomInBasket = (e: QwChangeRoomEvent) => {
    const occId = BasketHelper.getFirstOccupancyIdInBasketRoom(e.room);
    const { rateId, occupancyId } = e.room.occupancies[occId];
    BasketService.setRoomInBasket({
      quantity: parseInt(e.quantity),
      roomId: e.room.roomId,
      rateId,
      occupancyId,
    })
      .pipe(take(1))
      .subscribe();
  };

  weekDatesChanged = (e: QwWeekCalendarDirection) => {
    const dates = document.querySelectorAll('.qw-calendar-week__block-date');
    const prices = document.querySelectorAll('.qw-calendar-week__block-price');
    const selectedBlocks = document.querySelectorAll(
      '.qw-calendar-week__block--selected',
    );
    dates.forEach((d) => d.classList.remove('hide'));
    prices.forEach((d) => d.classList.remove('hide'));
    selectedBlocks.forEach((d) => d.classList.remove('hide'));

    this.isPriceLoading = true;
    const newRange =
      e === QwWeekCalendarDirection.Right
        ? this.getNextRangeOfDates(
            this.rangeDateString[this.rangeDateString.length - 1],
          )
        : this.getPrevRangeOfDates(this.rangeDateString[0]);

    this.getRoomsSearchForRange(newRange)
      .pipe(take(1))
      .subscribe((newRoomPrices) =>
        this.getRoomsSearchForRangeSuccess(newRoomPrices),
      );
  };

  private getNextRangeOfDates(date: string): SessionStayPeriod {
    const firstDateMinusOne = this.initNewDate(date);
    const firstDate = DateUtil.addDaysToDate(2, firstDateMinusOne);
    const lastDate = DateUtil.addDaysToDate(8, firstDate);
    return {
      arrivalDate: DateUtil.getDateStringFromDate(firstDate),
      departureDate: DateUtil.getDateStringFromDate(lastDate),
    };
  }

  private getPrevRangeOfDates(date: string) {
    const today = this.initNewDate(new Date());
    const lastDate = this.initNewDate(date);
    const shouldBeFirstDate = DateUtil.addDaysToDate(-6, lastDate);
    const firstDate = today >= shouldBeFirstDate ? today : shouldBeFirstDate;
    return {
      arrivalDate: DateUtil.getDateStringFromDate(firstDate),
      departureDate: DateUtil.getDateStringFromDate(lastDate),
    };
  }

  onAddedToBasket = (e: BasketModel) => {
    this.numberOfAccommodation = BasketHelper.getNumberOfAccommodation(e);
  };

  @Listen('roomTypeChange', { target: 'window' })
  public roomTypeChanged(event) {
    this.el.classList.add('is-filtering');
    this.filterRoomsByRoomType(event.detail.value);
    setTimeout(() => {
      this.el.classList.remove('is-filtering');
    }, 820);
  }

  private filterRoomsByRoomType(roomType: string): void {
    this.filteredRoomsByRoomType = this.rooms.map((room) => ({
      ...room,
      filterDisableRoom: this.filterRoom(room, roomType),
    }));

    this.setFilterAnimation();
  }

  private filterRoom(room: RoomModel, roomType: string): boolean {
    const isMatchingRoomType = this.compareRoomCodeRoomType(
      room.code,
      roomType,
    );

    if (this.filterRoomsAlreadyInBasket(room)) {
      return true;
    }

    const isSharedDorm =
      room.code.includes(QW_ROOM_CODE.Bunk) ||
      room.code.includes(QW_ROOM_CODE.BunkF);

    if (this.hasSessionChildrenOrInfants && isSharedDorm) {
      return true;
    }

    return !isMatchingRoomType ? false : true;
  }

  private compareRoomCodeRoomType(roomCode: string, roomType: string): boolean {
    const roomTypeMatchers: { [key: string]: () => boolean } = {
      [QW_ROOM_CODE.Bunk]: () => !roomCode.includes('--DORM'),
      [QW_ROOM_CODE.BunkF]: () => !roomCode.endsWith('F'),
      [QW_ROOM_CODE.Private]: () => !roomCode.includes('--PRIVATE'),
    };

    return roomTypeMatchers[roomType] ? roomTypeMatchers[roomType]() : false;
  }

  private setFilterAnimation() {
    this.qwRoomsHtmlElements.forEach((element) => {
      const height = element.offsetHeight;
      if (element.classList.contains('qw-room-list-card--hide') && height > 0) {
        element.style.maxHeight = height + 'px';
        requestAnimationFrame(() => {
          element.style.maxHeight = '0';
        });
      } else if (height == 0) {
        element.style.maxHeight = height + 'px';
        requestAnimationFrame(() => {
          element.style.maxHeight = null;
        });
      }
    });
  }

  public isPrivateRoom(roomCode: string) {
    return !!roomCode.includes('--PRIVATE');
  }

  private getRoomTypeFromSessionStorage() {
    return sessionStorageGetItem('roomType');
  }

  public getRoomAvailabilitySuccess(availability: RoomAvailability) {
    this.noAvailability = availability === RoomAvailability.noAvailability;
  }

  public emitGtm() {
    this.qwEmitGtmData.emit({
      event: 'GA4event',
      eventName: 'funnel_error',
      event_data: {
        pagename: 'joandjoe::reservations::step2::select a room',
        step_name: 'step2::error',
        error_type: 'no room available',
      },
    });
  }

  public removeFiltersFromStorage() {
    sessionStorageRemoveItem('filterRoomsBy');
  }

  public noRoomDisplayed(rooms) {
    return rooms.every((r) => r.filterDisableRoom == true);
  }

  public getCheapestCrossedOutPricePerNight(room: RoomModel) {
    const roomPrice = RoomHelper.getCheapestCrossedOutPrice(room);
    const roomPricePerNight = roomPrice.value.amount / this.nights;
    return `${
      MONEY_SYMBOLS[roomPrice.value.currency]
    } ${roundPriceToTwoDecimals(roomPricePerNight)}`;
  }

  render() {
    const dataExtraSkeleton = [
      {
        qwRepeat: 1,
        qwWidth: '95%',
        qwHeight: '24px',
        qwType: 'title',
      },
      {
        qwRepeat: 1,
        qwWidth: '65%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '45%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '24px',
        qwType: 'title',
      },
    ];
    const dataExtraSkeleton1 = [
      {
        qwRepeat: 1,
        qwWidth: '100%',
        qwHeight: '100%',
        qwType: 'image',
      },
    ];
    const dataExtraSkeleton2 = [
      {
        qwRepeat: 1,
        qwWidth: '60%',
        qwHeight: '24px',
        qwType: 'title',
      },
      {
        qwRepeat: 1,
        qwWidth: '65%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '28px',
        qwType: 'text',
      },
      {
        qwRepeat: 2,
        qwWidth: '40%',
        qwHeight: '30px',
        qwType: 'select',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '30px',
        qwType: 'select',
      },
    ];

    return (
      <Host
        class={`
        qw-room-list--${this.qwRoomListType}
        ${this.renderTriggered && !this.componentLoading ? 'visualize-list' : ''}
        ${this.renderTriggered && this.componentLoading ? 'loading-skeleton' : ''}
      `}
      >
        <div class="room-list-skeleton">
          <div class="skeleton-card">
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton1}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton2}
            ></qw-skeleton>
          </div>
          <div class="skeleton-card">
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton1}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton2}
            ></qw-skeleton>
          </div>
          <div class="skeleton-card">
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton1}
            ></qw-skeleton>
            <qw-skeleton
              class="qw-skeleton-room"
              qwData={dataExtraSkeleton2}
            ></qw-skeleton>
          </div>
        </div>

        {this.componentLoading && <div class="qw-loader"></div>}

        <div class="qw-room-list__cards">
          {(!this.componentLoading &&
            this.qwRoomListShowAvailabilityMessage &&
            this.noAvailability) ||
          (!this.componentLoading &&
            this.noRoomDisplayed(this.filteredRoomsByRoomType)) ? (
            <div>
              <qw-error class="qw-room-list__no-availability">
                {Language.getTranslation('noAvailabilityRooms')}
              </qw-error>
              {this.emitGtm()}
            </div>
          ) : (
            ''
          )}

          {/* //TODO CHECK IF ALL HIDE */}
          {!this.componentLoading &&
            this.hasSessionChildrenOrInfants &&
            !this.filteredRoomsByRoomType.length && (
              <div>{Language.getTranslation('noDormSelectable')}</div>
            )}

          {this.filteredRoomsByRoomType.map((r, index) => {
            return (
              r.rates && (
                <div
                  class={`qw-room-list__card-wrapper ${r.filterDisableRoom ? 'qw-room-list-card--hide' : ''}`}
                >
                  <qw-room-list-card
                    class={`
                  qw-room-list-card--${this.qwRoomListType}
                `}
                    room={r}
                    qwRoomListCardIndex={index}
                    qwRoomListCardRoom={r}
                    qwRoomListCardSession={this.session}
                    qwRoomListCardIsPrivateRoom={this.isPrivateRoom(r.code)}
                    qwRoomListCardShowCarouselInCard={
                      this.qwRoomListShowCarouselInCard
                    }
                    qwRoomListCardHighlightOffers={
                      this.qwRoomListHasPreselectedOffers
                    }
                    qwRoomListCardId={r.roomId}
                    qwRoomListCardTitle={r.name}
                    qwRoomListCardServices={r.services}
                    qwRoomListCardCarouselImages={RoomHelper.getCarouselImages(
                      r,
                    )}
                    qwRoomListCardImage={RoomHelper.getCoverImage(r)?.url}
                    qwRoomListCardBasketRoom={this.getBasketRoom(r.roomId)}
                    qwRoomListCardIsLoading={this.componentLoading}
                    qwRoomListCardDescription={RoomHelper.getSummary(r)?.text}
                    qwRoomListCardRangeDate={this.rangeDate}
                    qwRoomListCardRangeDateSession={this.rangeDateSession}
                    qwRoomListCardOnChangeRoom={(e) => this.setRoomInBasket(e)}
                    qwRoomListCardNumberOfGuests={this.numberOfGuests}
                    qwRoomListCardLanguage={this.currentLang}
                    qwRoomListCardPlaceholders={this.qwRoomListPlaceholders}
                    qwRoomListCardBaseInfoType={this.qwRoomListBaseInfoType}
                    qwRoomListCardRateHighlight={this.qwRoomListRateHighlight}
                    qwRoomListCardFilterRatesWithId={
                      this.qwRoomListFilterRatesWithId
                    }
                    qwRoomListCardImageTransformationOptions={
                      this.qwRoomListImageTransformationOptions
                        ? JSON.parse(this.qwRoomListImageTransformationOptions)
                        : {}
                    }
                    qwRoomListCardOnClickView={() =>
                      this.clickButton(QwRoomListCardButtonType.ViewRoom, r)
                    }
                    qwRoomListCardOnChangeWeekDates={(e) =>
                      this.weekDatesChanged(e)
                    }
                    qwRoomListCardOnAddedToBasket={(e) =>
                      this.onAddedToBasket(e)
                    }
                    qwRoomListCardRooms={this.rooms}
                    qwRoomListCardBasket={this.basket}
                  />
                </div>
              )
            );
          })}
        </div>
      </Host>
    );
  }
}
